import { Gear, Tag } from "@prisma/client";
import {
  Button,
  Col,
  Result,
  Row,
  Space,
  Spin,
  Tag as AntTag,
  Typography,
} from "antd";
import { AxiosInstance } from "axios";
import linkifyHtml from "linkifyjs/html";
import { FunctionComponent, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStateIfMounted } from "use-state-if-mounted";
import { ROUTE_DASHBOARD_GEARS } from "../../shared/app-constants";
import DeleteBtn from "../../shared/delete-btn";
import getDashboardRoute from "../../shared/get-dashboard-route";
import GuardedElement from "../../shared/guarded-element";
import Permissions from "../../shared/permissions";
import uDayjs from "../../shared/uDayjs";

interface GearDetailProps {
  id: string;
  httpClient: AxiosInstance | any;
  editPath: string;
  homepage: string;
  onDelete?: (gear: Gear) => void;
  onChange?: (gear: Gear) => void;
}

const GearDetail: FunctionComponent<GearDetailProps> = ({
  id,
  httpClient,
  editPath,
  homepage,
}) => {
  const [loading, setLoading] = useStateIfMounted(true);
  const [gear, setGear] = useStateIfMounted<Gear | undefined>(undefined);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      setLoading(true);
      setGear(undefined);
      try {
        const response = await httpClient.get(`gear/${id}`);

        if (response.data?.data) {
          setGear(response.data.data);
        }
      } catch (e) {}
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    const onImageClick = (e: any) => {
      // on img click, if the image isn't inside a link, open it in a new tab
      if (e.target.tagName === "IMG" && !e.target.closest("a")) {
        console.log("opening image in new tab", e.target.src);
        window.open(e.target.src, "_blank");
      }
    };
    addEventListener("click", onImageClick);
    return () => removeEventListener("click", onImageClick);
  }, []);

  return (
    <div>
      <Row>
        <Col xs={24}>
          {!loading && !gear && (
            <Result
              status={404}
              title="Gear not found!"
              subTitle="The gear you looking for can't be found!"
              extra={
                <Link to={{ pathname: getDashboardRoute({}) }}>
                  <Button type="link">Go Back</Button>
                </Link>
              }
            />
          )}
          {loading && <Spin />}
          {!loading && gear && (
            <table className="gear-table">
              <tbody>
                <tr>
                  <th>Name</th>
                  <td>{gear.name}</td>
                </tr>
                <tr>
                  <th>Description</th>
                  <td className="ql-snow">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: linkifyHtml(gear.description || "--"),
                      }}
                      className="gear-description ql-editor"
                    ></div>
                  </td>
                </tr>
                <tr>
                  <th>Urls</th>
                  <td>
                    {(gear as any)?.urls
                      ?.map(({ root_url, contains }: any) => {
                        const tmp: string[] = [];
                        if (root_url) {
                          tmp.push(root_url);
                        }
                        if (contains) {
                          tmp.push(contains);
                        }
                        return tmp.join(":");
                      })
                      ?.join(", ")}
                  </td>
                </tr>
                <tr>
                  <th>Tags</th>
                  <td>
                    {(gear as any)?.tags?.map((e: Tag) => (
                      <Link
                        key={e.id}
                        to={{
                          pathname: getDashboardRoute(
                            {},
                            ROUTE_DASHBOARD_GEARS
                          ),
                          search: `?tags=${encodeURIComponent(
                            `${e.id}|${e.name}`
                          )}`,
                        }}
                      >
                        <AntTag>{e.name}</AntTag>
                      </Link>
                    ))}
                  </td>
                </tr>
                <tr>
                  <th>Created By</th>
                  <td>
                    <div>
                      <Typography.Text>
                        {(gear as any)?.created_by?.name}
                      </Typography.Text>{" "}
                    </div>
                    <Typography.Text type="secondary">
                      {uDayjs(gear.created_at).format(`DD/MM/YYYY hh:mma`)}
                    </Typography.Text>
                  </td>
                </tr>
                <tr>
                  <th>Updated By</th>
                  <td>
                    <div>
                      <Typography.Text>
                        {(gear as any)?.updated_by?.name}
                      </Typography.Text>{" "}
                    </div>
                    <Typography.Text type="secondary">
                      {uDayjs(gear.updated_at).format(`DD/MM/YYYY hh:mma`)}
                    </Typography.Text>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <Space>
                      <GuardedElement permissions={[Permissions.EDIT_GEAR]}>
                        <Button type="default" size="small">
                          <Link to={{ pathname: editPath }}>Edit</Link>
                        </Button>
                      </GuardedElement>
                      <GuardedElement permissions={[Permissions.DELETE_GEAR]}>
                        <DeleteBtn
                          path={`gear/${gear.id}`}
                          httpClient={httpClient}
                          onDone={() => {
                            navigate({ pathname: homepage });
                          }}
                        />
                      </GuardedElement>
                    </Space>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default GearDetail;
